import { Button } from '@creditornot/cb-button';
import { cbSemanticCssVars } from '@creditornot/cb-ingredients';
import { EmptyIcon } from '@creditornot/cb-placeholders';
import { LegacyPopover } from '@creditornot/cb-popover';
import { Select } from '@creditornot/cb-select';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import React, { useCallback, useState } from 'react';
import toast from 'react-hot-toast';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';

import { Typography as T } from 'common/typography';
import { languages } from 'config';
import { createSharedMenu } from 'modules/shared-menu/api';
import { languageToPhraseKeyMap } from 'modules/shared-menu/constants';
import { CreateSharedMenuPayload } from 'modules/shared-menu/types';

type LegacyCreateVenueSelectionProps = {
  defaultLanguage: Language | undefined;
  hasCreateMenuPermission: boolean;
  venueId: string;
  venueSlug: string;
  onCreateSuccess: (id: string) => void;
};

const Root = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  align-items: center;
  background-color: ${cbSemanticCssVars.surfaceMain};
  padding-block-start: 64px;
`;

const MenuIcon = styled(EmptyIcon.FileText)`
  margin-block-end: 16px;
`;

const Title = styled(T.Body2)`
  font-weight: bold;
  color: ${cbSemanticCssVars.textSecondary};
  margin-block-end: 8px;
`;

const SubTitle = styled(T.Small)`
  color: ${cbSemanticCssVars.textSecondary};
  margin-block-end: 32px;
`;

const LanguageSelect = styled(Select)`
  width: 240px;
  margin-block-end: 16px;
`;

const CreateButton = styled(Button).attrs({ size: 'small', variant: 'primary' })``;

const NoPermission = styled.div`
  max-width: 300px;
  padding: 10px;
`;

export const LegacyCreateVenueSelection = ({
  defaultLanguage,
  hasCreateMenuPermission,
  venueId,
  venueSlug,
  onCreateSuccess,
}: LegacyCreateVenueSelectionProps) => {
  const [language, setLanguage] = useState<Language | undefined>(defaultLanguage);
  const intl = useIntl();
  const t = (id: string) => intl.formatMessage({ id });
  const queryClient = useQueryClient();
  const options = languages.map(lang => ({ value: lang, label: t(languageToPhraseKeyMap[lang]) }));

  const { mutateAsync: executeCreateSelection, isLoading: isLoadingCreatingSelection } = useMutation(
    (payload: CreateSharedMenuPayload) => createSharedMenu(payload),
    {
      onSuccess: selection => {
        queryClient.invalidateQueries(['shared-menu-link', venueId]);
        onCreateSuccess(selection.id);

        toast.success(<FormattedMessage id="toast.success" />);
      },
      onError: () => {
        toast.error(<FormattedMessage id="errors.unknown-error-retry-contact" />, { duration: Infinity });
      },
    },
  );

  const handleCreateMenu = useCallback(async () => {
    if (!language) {
      return;
    }

    const payload: CreateSharedMenuPayload = {
      name: [
        {
          // adds a default name to single venue's menus (not displayed in the UI)
          value: `${venueSlug}'s menu`,
          lang: language,
          verified: true,
        },
      ],
      primary_language: language,
      scope: {
        type: 'venue',
        id: venueId,
      },
    };

    try {
      await executeCreateSelection(payload);
      // eslint-disable-next-line no-empty
    } catch {}
  }, [executeCreateSelection, language, venueId, venueSlug]);

  const isLoading = isLoadingCreatingSelection;

  return (
    <Root>
      <MenuIcon />
      <Title>{t('menu.noMenuForVenue')}</Title>
      <SubTitle>{t('menu.createEmptyMenuInfo')}</SubTitle>
      <LanguageSelect
        disabled={isLoading}
        value={language}
        options={options}
        clearable={false}
        placeholder={t('menu.selectLanguage')}
        onChange={(lang: Language) => setLanguage(lang)}
      />
      <LegacyPopover
        disabled={hasCreateMenuPermission}
        content={<NoPermission>{t('menu.no-create-menu-permission')}</NoPermission>}
        placement="bottom"
        renderArrow
      >
        <CreateButton
          disabled={language === undefined || isLoading || !hasCreateMenuPermission}
          onClick={handleCreateMenu}
          loading={isLoading}
        >
          {t('menu.createEmptyMenu')}
        </CreateButton>
      </LegacyPopover>
    </Root>
  );
};
